import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import { useDeskData } from "../../contexts/DeskDataContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { FormField, isFieldValid } from "../common/FormField";
import { SubmitButton } from "../common/SubmitButton";
import {
  MemberPersonalFields, EMPTY_PERSONAL_FORM, isPersonalFormValid, buildPersonalPayload,
} from "./MemberPersonalFields";
import { MembershipPaymentStep } from "./MembershipPaymentStep";
import { HealthGoalsFields } from "./HealthGoalsFields";

const FAMILY_PLAN_PAYMENT_TEST_IDS = {
  planSelect: DESK.familyPlanSelect, startDateInput: DESK.familyStartDateInput, discountInput: DESK.familyDiscountInput,
  planNextButton: DESK.familyPlanNextButton, skipButton: DESK.familySkipPlanButton, paymentMode: DESK.familyPaymentMode,
  txnRefInput: DESK.familyTxnRefInput, paymentAmount: DESK.familyPaymentAmount, netPayable: DESK.familyNetPayable,
  outstandingBalance: DESK.familyOutstandingBalance, paymentSubmit: DESK.familyPaymentSubmit,
  invoiceButton: DESK.familyInvoiceButton, continueButton: DESK.familyPaymentContinueButton,
};

// UX-FD-01 §1 SCR-P02-14: search by phone first — if the family member already has a member
// record, link them straight in (unchanged, short path). Only a genuinely brand-new phone number
// goes through the fuller 3-step onboarding (Personal -> Membership & payment (skippable, per
// product decision — a family member can get their plan later via the existing Add-plan flow on
// their own member page) -> Health & goals (always skippable)), mirroring the Walk-in flow.
export function AddFamilyModal({ member, open, onOpenChange }) {
  const { branchId } = useDeskData();
  const [phase, setPhase] = useState("search"); // search -> personal -> plan_payment -> health
  const [phone, setPhone] = useState("");
  const [found, setFound] = useState(undefined); // undefined = not searched, null = searched-not-found, object = found
  const [newForm, setNewForm] = useState({ name: "" });
  const [personalForm, setPersonalForm] = useState(EMPTY_PERSONAL_FORM);
  const [newMember, setNewMember] = useState(null);
  const queryClient = useQueryClient();

  const reset = () => {
    setPhase("search"); setPhone(""); setFound(undefined); setNewForm({ name: "" });
    setPersonalForm(EMPTY_PERSONAL_FORM); setNewMember(null);
  };

  const close = () => { onOpenChange(false); reset(); };
  const finish = (message) => {
    toast.success(message);
    queryClient.invalidateQueries({ queryKey: ["desk-member", member.id] });
    close();
  };

  const search = async () => {
    const res = await api.get("/members", { params: { phone } });
    setFound(res.data.members[0] || null);
  };

  const linkMutation = useMutation({
    mutationFn: () => api.post("/family-groups", { parent_member_ref: member.id, member_refs: [found.id] }),
    onSuccess: () => finish("Family member added."),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const createAndLinkMutation = useMutation({
    mutationFn: async () => {
      const created = (await api.post("/members", { name: newForm.name, phone, branch_id: branchId, ...buildPersonalPayload(personalForm) })).data;
      await api.post("/family-groups", { parent_member_ref: member.id, member_refs: [created.id] });
      return created;
    },
    onSuccess: (created) => { setNewMember(created); setPhase("plan_payment"); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const newDetailsValid = isFieldValid("text", true, newForm.name) && isPersonalFormValid(personalForm);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(v); }}>
      <DialogContent className="glass-modal max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add family member</DialogTitle>
          <DialogDescription>
            {phase === "search" && `Link an existing member, or create a new one, under ${member?.profile?.name}'s family group.`}
            {phase === "personal" && "Step 1 of 3 — Personal details"}
            {phase === "plan_payment" && "Step 2 of 3 — Membership & payment (optional)"}
            {phase === "health" && "Step 3 of 3 — Health & goals (optional)"}
          </DialogDescription>
        </DialogHeader>

        {phase === "search" && (
          <>
            <div className="space-y-3">
              <div className="flex gap-2 items-start">
                <FormField required type="tel" label="Their phone number" testId={DESK.familyMemberInput} value={phone} onChange={(v) => { setPhone(v); setFound(undefined); }} className="flex-1" />
                <Button variant="outline" onClick={search} disabled={!isFieldValid("tel", true, phone)} className="mt-6">Search</Button>
              </div>
              {found === null && (
                <p className="text-sm" style={{ color: "#68686E" }}>No match — continue to add them as a new member.</p>
              )}
              {found && <p className="text-sm" style={{ color: "#141416" }}>Found: <span className="font-semibold">{found.profile.name}</span></p>}
            </div>
            <DialogFooter>
              {found === null ? (
                <Button data-testid={DESK.familySubmitButton} onClick={() => setPhase("personal")} className="text-white" style={{ background: "#D6202B" }}>
                  Continue
                </Button>
              ) : (
                <SubmitButton
                  testId={DESK.familySubmitButton} disabled={found === undefined}
                  pending={linkMutation.isPending} onClick={() => linkMutation.mutate()}
                  label="Add to family" successLabel="Added"
                />
              )}
            </DialogFooter>
          </>
        )}

        {phase === "personal" && (
          <>
            <div className="space-y-4">
              <FormField label="Full name" required testId={DESK.familyNewNameInput} value={newForm.name} onChange={(v) => setNewForm({ name: v })} />
              <MemberPersonalFields form={personalForm} onChange={(k, v) => setPersonalForm((f) => ({ ...f, [k]: v }))} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPhase("search")}>Back</Button>
              <SubmitButton
                testId={DESK.familySubmitButton} disabled={!newDetailsValid}
                pending={createAndLinkMutation.isPending} onClick={() => createAndLinkMutation.mutate()}
                label="Continue"
              />
            </DialogFooter>
          </>
        )}

        {phase === "plan_payment" && (
          <MembershipPaymentStep
            member={newMember} branchId={branchId} allowSkip testIds={FAMILY_PLAN_PAYMENT_TEST_IDS}
            onSkip={() => setPhase("health")}
            onComplete={() => setPhase("health")}
          />
        )}

        {phase === "health" && (
          <HealthGoalsFields member={newMember} pageTestId={DESK.familyStep(3)} onDone={() => finish(`${newMember?.profile?.name} added to the family.`)} />
        )}
      </DialogContent>
    </Dialog>
  );
}
